/**
 * TextArea Component
 *
 * Reusable multi-line text input
 * - Auto-resize option
 * - Character count
 * - Error state
 * - Disabled state
 */

import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import "./TextArea.scss";

export const TextArea = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  error = false,
  rows = 4,
  maxLength,
  autoResize = false,
  showCount = false,
  fullWidth = true,
  className = "",
  ...props
}) => {
  const textAreaRef = useRef(null);

  // Auto-resize functionality
  useEffect(() => {
    if (autoResize && textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [value, autoResize]);

  const textAreaClasses = [
    "textarea",
    error && "textarea--error",
    disabled && "textarea--disabled",
    fullWidth && "textarea--full-width",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="textarea-wrapper">
      <textarea
        ref={textAreaRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        className={textAreaClasses}
        {...props}
      />
      {showCount && maxLength && (
        <div className="textarea-count">
          {value?.length || 0} / {maxLength}
        </div>
      )}
    </div>
  );
};

TextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  rows: PropTypes.number,
  maxLength: PropTypes.number,
  autoResize: PropTypes.bool,
  showCount: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
};

export default TextArea;
